import { defineStore } from 'pinia';
import { ref, computed, reactive } from 'vue';
import { ElMessage } from 'element-plus';
import { getSessionExpiry, isSessionExpired, sessionExpiresAt } from '../session';

const SESSION_STORAGE_KEY = 'matou_admin_token';
const SESSION_EXPIRY_STORAGE_KEY = 'matou_admin_session_expires_at';

function readSessionStorage(_key: string): string {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(_key) ?? '' : '';
  } catch {
    return '';
  }
}

export type SaleMode = "FIXED" | "QUOTE";
export type BillingUnit = "PIECE" | "JIN";
export type FulfillmentType = "PICKUP" | "EXPRESS";
export type OrderStatus = "PENDING_PAYMENT" | "PAID" | "PROCESSING" | "READY_FOR_PICKUP" | "REFUND_REVIEW" | "SHIPPED" | "COMPLETED" | "CANCELLED" | "REFUNDING" | "REFUNDED" | "FULFILLING";
export type OfflineFreightStatus = "PENDING_CONTACT" | "AGREED_AWAITING_QUOTE" | "PENDING_OFFLINE_PAYMENT" | "OFFLINE_PAID" | "DECLINED";

export interface ProductImage { path: string; imageUrl?: string | null; alt?: string | null; sortOrder?: number; isPrimary?: boolean }
export interface Product { id: string; title: string; saleMode: SaleMode; billingUnit?: BillingUnit; priceCents: number | null; stock: number; active: boolean; imagePath?: string | null; imageUrl?: string | null; images?: ProductImage[]; categoryId?: string | null; fulfillmentTypes?: FulfillmentType[] }
export interface OrderLine { title: string; titleSnapshot?: string; quantity: number; quantityMinor?: number; billingUnit?: BillingUnit; unitPriceCents?: number }
export interface OrderAdjustment { id: string; type: "REFUND"; amountCents: number; note: string; status: "PENDING" | "PROCESSING" | "SUCCEEDED" | "FAILED" | "CANCELLED"; createdBy?: string; failureReason?: string | null; outTradeNo?: string; refundNo?: string; createdAt?: number; updatedAt?: number }
export interface Shipment { carrierCode: "JD" | "SF" | string; trackingNo: string; status: string; wechatSyncedAt?: number }
export interface RefundReview { id: string; source: "CUSTOMER" | "AUTO_TIMEOUT"; status: "PENDING" | "APPROVED" | "REJECTED"; amountCents: number; decisionNote?: string | null; urgentAt?: number | null; createdAt: number; decidedAt?: number | null }
export interface Order { orderNo: string; customerOpenId?: string; contactPhone?: string; buyerContactPhone?: string; saleMode: SaleMode; billingUnit?: BillingUnit; fulfillmentType: FulfillmentType; goodsTotalCents?: number; freightCents?: number; totalCents: number; status: OrderStatus; freightMode?: "PLATFORM_TEMPLATE" | "OFF_PLATFORM_MANUAL"; offlineFreightStatus?: OfflineFreightStatus; expressFreightStatus?: OfflineFreightStatus; offlineFreightCents?: number | null; offlineFreightNote?: string | null; deliveryAddress?: { recipientName: string; phone: string; province: string; city: string; district: string; detail: string }; pickupVerification?: { code: string; qrPayload: string; issuedAt: number; verifiedAt?: number }; shipment?: Shipment; lines?: OrderLine[]; adjustments?: OrderAdjustment[]; refundReviews?: RefundReview[] }
export interface OrderEvent { eventId: string; eventType: "ORDER_PAID" | "ORDER_CREATED" | "ORDER_STATUS_CHANGED" | "REFUND_REVIEW_REQUESTED" | "REFUND_REVIEW_URGENT"; orderNo: string; fulfillmentType: FulfillmentType; totalCents: number; createdAt: number }
export interface NotificationStatus { status: "NOT_CONFIGURED" | "NO_RECIPIENTS" | "READY" | "AUTH_EXPIRED" | "FAILED"; backoffice: "READY"; message: string; recipientsCount?: number; templateConfigured?: boolean; lastErrorCode?: string; lastErrorAt?: number; subscriptionMode?: "ONE_TIME" | "LONG_TERM" }
export interface NotificationRecipient { id: string; label?: string; active: boolean; remainingCount?: number; boundAt?: number; lastSentAt?: number; lastErrorCode?: string; lastErrorAt?: number; createdAt: number; updatedAt: number }
export interface NotificationBinding { ticket: string; expiresAt: number; sharePath?: string }
export interface Quote { id: string; customerOpenId: string; productId: string; quantity: number; fulfillmentType: FulfillmentType; status: "PENDING" | "CONFIRMED" | "EXPIRED" | "CANCELLED"; expiresAt: number; orderId?: string }
export interface StoreProfile { name: string; address: string; contactPhone?: string | null; pickupInstructions?: string | null; businessHours?: string | null }

export const useAdminStore = defineStore('admin', () => {
  const apiBase = String(import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
  const products = ref<Product[]>([]);
  const categories = ref<Array<{ id: string; name: string; sortOrder?: number; active?: boolean }>>([]);
  const orders = ref<Order[]>([]);
  const quotes = ref<Quote[]>([]);
  
  const loading = ref(false);
  const orderLoading = ref(false);
  const quoteLoading = ref(false);
  const publishLoading = ref(false);
  const storeSaving = ref(false);
  const errorMessage = ref("");

  const activeMenu = ref<"经营概览" | "商品管理" | "发货退款" | "店铺设置">("经营概览");
  const targetOrderTab = ref<"ALL" | "PENDING" | "PICKUP" | "SHIPPED" | "REFUND" | "">("");
  const adminPasscode = ref("");
  const initialSessionToken = readSessionStorage(SESSION_STORAGE_KEY);
  const initialStoredExpiry = Number(readSessionStorage(SESSION_EXPIRY_STORAGE_KEY));
  const adminSessionToken = ref(initialSessionToken);
  const adminSessionExpiresAt = ref(Number.isFinite(initialStoredExpiry) && initialStoredExpiry > 0 ? initialStoredExpiry : getSessionExpiry(initialSessionToken));
  const adminTokenVerified = ref(false);
  const adminSessionChecking = ref(initialSessionToken.length > 0);
  const adminLoginLoading = ref(false);
  let expiryTimer: ReturnType<typeof setTimeout> | undefined;
  let monitorInstalled = false;
  let sessionMonitorHandler: (() => void) | undefined;
  let orderEventTimer: ReturnType<typeof setInterval> | undefined;
  const lastOrderEventAt = ref(0);
  const unreadOrderEvents = ref<OrderEvent[]>([]);
  const notificationStatus = ref<NotificationStatus>({ status: "NOT_CONFIGURED", backoffice: "READY", message: "微信模板或字段映射未配置，后台实时提醒正常工作" });
  const notificationRecipients = ref<NotificationRecipient[]>([]);
  const notificationBinding = ref<NotificationBinding | null>(null);

  const storeProfile = ref<StoreProfile>({ name: "", address: "", contactPhone: "", pickupInstructions: "", businessHours: "" });

  const quoteDrafts = reactive<Record<string, { unitPriceYuan: string }>>({});

  const apiReady = computed(() => apiBase.length > 0);
  const pendingShipments = computed(() => orders.value.filter((order) => ["PAID", "PROCESSING", "READY_FOR_PICKUP", "FULFILLING"].includes(order.status)).length);
  const pickupReady = computed(() => orders.value.filter((order) => order.status === "READY_FOR_PICKUP").length);
  const pendingRefunds = computed(() => orders.value.filter((order) => order.status === "REFUND_REVIEW" || order.status === "REFUNDING" || (order.adjustments ?? []).some((item) => item.type === "REFUND" && ["PENDING", "PROCESSING"].includes(item.status))).length);
  const pendingAdjustments = pendingRefunds;
  const pendingTasksCount = computed(() => pendingShipments.value + pendingRefunds.value);

  function clearExpiryTimer() {
    if (expiryTimer !== undefined) clearTimeout(expiryTimer);
    expiryTimer = undefined;
  }

  function persistSessionExpiry() {
    try {
      if (typeof sessionStorage === 'undefined') return;
      if (adminSessionExpiresAt.value) sessionStorage.setItem(SESSION_EXPIRY_STORAGE_KEY, String(adminSessionExpiresAt.value));
      else sessionStorage.removeItem(SESSION_EXPIRY_STORAGE_KEY);
    } catch {
      // Session storage may be unavailable in private browsing; in-memory state still works.
    }
  }

  function clearAdminSession(_notify = false) {
    clearExpiryTimer();
    adminSessionToken.value = "";
    adminSessionExpiresAt.value = undefined;
    adminTokenVerified.value = false;
    adminSessionChecking.value = false;
    lastOrderEventAt.value = 0;
    unreadOrderEvents.value = [];
    if (orderEventTimer) clearInterval(orderEventTimer);
    orderEventTimer = undefined;
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
        sessionStorage.removeItem(SESSION_EXPIRY_STORAGE_KEY);
      }
    } catch {
      // Ignore storage cleanup failures.
    }
    if (_notify) ElMessage.error("登录已过期，请重新登录");
  }

  function scheduleSessionExpiry() {
    clearExpiryTimer();
    if (!adminSessionToken.value || !adminSessionExpiresAt.value) return;
    const delay = Math.max(0, adminSessionExpiresAt.value - Date.now());
    expiryTimer = setTimeout(() => {
      if (isSessionExpired(adminSessionExpiresAt.value)) clearAdminSession(true);
      else scheduleSessionExpiry();
    }, Math.min(delay + 10, 2_147_483_647));
  }

  function checkSessionExpiry(_notify = false): boolean {
    if (!adminSessionToken.value) return false;
    if (isSessionExpired(adminSessionExpiresAt.value)) {
      clearAdminSession(_notify);
      return false;
    }
    return true;
  }

  function startSessionMonitor() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (!monitorInstalled) {
      sessionMonitorHandler = () => { checkSessionExpiry(true); };
      window.addEventListener('focus', sessionMonitorHandler);
      document.addEventListener('visibilitychange', sessionMonitorHandler);
      monitorInstalled = true;
    }
    checkSessionExpiry(false);
    scheduleSessionExpiry();
    startOrderEventMonitor();
    if (adminTokenVerified.value) void loadNotificationStatus();
  }

  function stopSessionMonitor() {
    if (!monitorInstalled || typeof window === 'undefined' || typeof document === 'undefined') return;
    if (sessionMonitorHandler) {
      window.removeEventListener('focus', sessionMonitorHandler);
      document.removeEventListener('visibilitychange', sessionMonitorHandler);
    }
    sessionMonitorHandler = undefined;
    monitorInstalled = false;
    clearExpiryTimer();
    if (orderEventTimer) clearInterval(orderEventTimer);
    orderEventTimer = undefined;
  }

  function notifyOrderEvent(_event: OrderEvent) {
    const isRefundReview = _event.eventType === "REFUND_REVIEW_REQUESTED" || _event.eventType === "REFUND_REVIEW_URGENT";
    const message = isRefundReview
      ? `快递退款${_event.eventType === "REFUND_REVIEW_URGENT" ? "超期" : "申请"}待审核：${_event.orderNo.slice(-4)}`
      : `新订单已支付：${_event.orderNo.slice(-4)}，请及时处理`;
    isRefundReview ? ElMessage.warning(message) : ElMessage.success(message);
    try {
      const AudioContextCtor = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (AudioContextCtor) {
        const context = new AudioContextCtor();
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.frequency.value = 880;
        gain.gain.value = 0.08;
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start();
        oscillator.stop(context.currentTime + 0.18);
      }
    } catch { /* 音频权限不足时降级为页面提示 */ }
    try {
      if (document.visibilityState !== "visible" && "Notification" in window && Notification.permission === "granted") {
        new Notification(isRefundReview ? "码头海鲜超市退款待审核" : "码头海鲜超市新订单", { body: message });
      }
    } catch { /* 浏览器通知不可用时不影响业务 */ }
  }

  async function pollOrderEvents() {
    if (!adminTokenVerified.value) return;
    try {
      const isInitialSync = lastOrderEventAt.value === 0;
      const response = await adminFetch(`/api/admin/order-events?since=${encodeURIComponent(String(lastOrderEventAt.value))}`);
      const body = await response.json() as { items?: OrderEvent[] };
      const events = body.items ?? [];
      if (isInitialSync) {
        lastOrderEventAt.value = Math.max(Date.now(), ...events.map((event) => event.createdAt));
        return;
      }
      if (!events.length) return;
      lastOrderEventAt.value = Math.max(lastOrderEventAt.value, ...events.map((event) => event.createdAt));
      unreadOrderEvents.value.push(...events);
      for (const event of events) notifyOrderEvent(event);
      if (activeMenu.value === "发货退款" || activeMenu.value === "经营概览") await loadOrders();
    } catch { /* 提醒轮询失败时不阻断后台主流程 */ }
  }

  function startOrderEventMonitor() {
    if (typeof window === "undefined" || orderEventTimer || !adminTokenVerified.value) return;
    void pollOrderEvents();
    orderEventTimer = setInterval(() => { void pollOrderEvents(); }, 12000);
  }

  function clearUnreadOrderEvents() {
    unreadOrderEvents.value = [];
  }

  function authHeaders(body?: BodyInit | null): HeadersInit {
    if (!checkSessionExpiry(true) || !adminSessionToken.value.trim()) throw new Error("请先登录后台");
    const headers: Record<string, string> = { Authorization: `Bearer ${adminSessionToken.value.trim()}` };
    if (!(typeof FormData !== "undefined" && body instanceof FormData)) headers["Content-Type"] = "application/json";
    return headers;
  }

  async function adminFetch(path: string, init: RequestInit = {}) {
    if (!apiReady.value) throw new Error("后端 API 域名尚未配置");
    let response: Response;
    try {
      response = await fetch(`${apiBase}${path}`, { ...init, headers: { ...authHeaders(init.body), ...(init.headers ?? {}) } });
    } catch (error) {
      adminSessionChecking.value = false;
      throw error;
    }
    if (response.status === 401 || response.status === 403) {
      clearAdminSession(true);
      throw new Error("登录已过期，请重新登录");
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { message?: string | string[]; code?: string };
      throw new Error(Array.isArray(body.message) ? body.message.join("；") : body.message ?? body.code ?? `HTTP ${response.status}`);
    }
    adminTokenVerified.value = true;
    adminSessionChecking.value = false;
    if (!adminSessionExpiresAt.value) {
      adminSessionExpiresAt.value = getSessionExpiry(adminSessionToken.value);
      persistSessionExpiry();
      scheduleSessionExpiry();
    }
    return response;
  }

  async function loginAdmin() {
    if (!apiReady.value) return ElMessage.warning("请先配置后台 API 域名");
    if (!adminPasscode.value.trim()) return ElMessage.warning("请输入后台管理口令");
    adminLoginLoading.value = true;
    try {
      const response = await fetch(`${apiBase}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ passcode: adminPasscode.value.trim() })
      });
      const body = await response.json().catch(() => ({})) as { token?: string; expiresInSeconds?: number; message?: string | string[]; code?: string };
      if (!response.ok || !body.token) throw new Error(Array.isArray(body.message) ? body.message.join("；") : body.message ?? body.code ?? `HTTP ${response.status}`);
      
      adminSessionToken.value = body.token;
      adminSessionExpiresAt.value = sessionExpiresAt(body) ?? getSessionExpiry(body.token);
      adminTokenVerified.value = true;
      adminSessionChecking.value = false;
      try {
        if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(SESSION_STORAGE_KEY, body.token);
      } catch {
        // In-memory login remains usable if session storage is blocked.
      }
      persistSessionExpiry();
      scheduleSessionExpiry();
      startOrderEventMonitor();
      void loadNotificationStatus();
      adminPasscode.value = "";
      ElMessage.success("登录成功");
      await refreshCurrent();
    } catch (error) {
      clearAdminSession(false);
      ElMessage.error(error instanceof Error ? error.message : "登录失败");
    } finally {
      adminLoginLoading.value = false;
    }
  }

  function onTokenInput() {
    clearAdminSession(false);
  }

  function logoutAdmin() {
    clearAdminSession(false);
    adminPasscode.value = "";
    ElMessage.info("已退出登录");
  }

  async function loadProducts() {
    if (!apiReady.value) { errorMessage.value = "后端 API 域名尚未配置；为避免伪造经营数据，当前只显示空状态。"; return; }
    loading.value = true;
    try {
      const response = await fetch(`${apiBase}/api/catalog/products`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const body = await response.json() as { items?: Product[] };
      products.value = body.items ?? [];
      errorMessage.value = "";
    } catch (error) { errorMessage.value = error instanceof Error ? `商品读取失败：${error.message}` : "商品读取失败"; }
    finally { loading.value = false; }
  }

  async function loadCategories() {
    try {
      const response = await fetch(`${apiBase}/api/catalog/categories`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      categories.value = ((await response.json()) as { items?: Array<{ id: string; name: string; sortOrder?: number; active?: boolean }> }).items ?? [];
    } catch { categories.value = []; }
  }

  async function loadQuotes() {
    quoteLoading.value = true;
    try {
      quotes.value = ((await (await adminFetch("/api/admin/quotes")).json()) as { items?: Quote[] }).items ?? [];
      for (const quote of quotes.value) quoteDrafts[quote.id] ??= { unitPriceYuan: "" };
      errorMessage.value = "";
    }
    catch (error) { errorMessage.value = error instanceof Error ? `询价读取失败：${error.message}` : "询价读取失败"; }
    finally { quoteLoading.value = false; }
  }

  async function loadOrders() {
    orderLoading.value = true;
    try {
      const items = ((await (await adminFetch("/api/admin/orders")).json()) as { items?: Order[] }).items ?? [];
      orders.value = items.filter((order) => order.status !== "PENDING_PAYMENT");
      errorMessage.value = "";
    } catch (error) { errorMessage.value = error instanceof Error ? `订单读取失败：${error.message}` : "订单读取失败"; }
    finally { orderLoading.value = false; }
  }

  async function loadNotificationStatus() {
    try {
      notificationStatus.value = await (await adminFetch("/api/admin/notification-status")).json() as NotificationStatus;
    } catch {
      // 状态面板只做提示，不影响订单列表与提醒轮询。
    }
  }

  async function loadNotificationRecipients() {
    try {
      notificationRecipients.value = ((await (await adminFetch("/api/admin/notification-recipients")).json()) as { items?: NotificationRecipient[] }).items ?? [];
    } catch (error) {
      errorMessage.value = error instanceof Error ? `商家提醒接收人读取失败：${error.message}` : "商家提醒接收人读取失败";
    }
  }

  async function createNotificationBinding() {
    const response = await adminFetch("/api/admin/notification-bindings", { method: "POST" });
    notificationBinding.value = await response.json() as NotificationBinding;
    return notificationBinding.value;
  }

  async function disableNotificationRecipient(id: string) {
    await adminFetch(`/api/admin/notification-recipients/${encodeURIComponent(id)}`, { method: "DELETE" });
    await Promise.all([loadNotificationRecipients(), loadNotificationStatus()]);
  }

  async function enableDesktopNotifications() {
    if (typeof window === "undefined" || !("Notification" in window)) {
      ElMessage.info("当前浏览器不支持桌面提醒，页面提示和声音提醒仍会保留");
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      ElMessage.info(permission === "granted" ? "桌面提醒已启用" : "未开启桌面提醒，页面提示和声音提醒仍会保留");
    } catch {
      ElMessage.info("桌面提醒未开启，页面提示和声音提醒仍会保留");
    }
  }

  async function loadStore() {
    try {
      const response = await adminFetch("/api/admin/store");
      const body = await response.json() as { profile?: StoreProfile };
      if (body.profile) { storeProfile.value = { ...storeProfile.value, ...body.profile }; }
    } catch (error) { errorMessage.value = error instanceof Error ? `门店资料读取失败：${error.message}` : "门店资料读取失败"; }
  }

  async function refreshCurrent() {
    if (activeMenu.value === "商品管理") await Promise.all([loadProducts(), loadCategories()]);
    if (activeMenu.value === "发货退款") await loadOrders();
    if (activeMenu.value === "店铺设置") await Promise.all([loadStore(), loadCategories(), loadNotificationStatus(), loadNotificationRecipients()]);
    if (activeMenu.value === "经营概览") {
      await loadProducts();
      if (adminSessionToken.value.trim()) await Promise.all([loadQuotes(), loadOrders(), loadStore()]);
    }
  }

  function selectMenu(menu: "经营概览" | "商品管理" | "发货退款" | "店铺设置") {
    activeMenu.value = menu;
    if (menu === "发货退款") clearUnreadOrderEvents();
    void refreshCurrent();
  }

  // Unit conversion utils
  function yuanToCents(value: string | number): number | null {
    const normalized = String(value).trim();
    if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) return null;
    const cents = Math.round(Number(normalized) * 100);
    return Number.isSafeInteger(cents) ? cents : null;
  }
  function centsInput(cents: number) { return (cents / 100).toFixed(2); }
  function money(cents: number | null | undefined, unit: BillingUnit = "PIECE") { return cents == null ? "待补价" : `¥${centsInput(cents)}/${unit === "JIN" ? "斤" : "件"}`; }
  function productImageUrl(product: Product) {
    const firstImage = product.images?.slice().sort((left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0))[0];
    const source = product.imageUrl ?? product.imagePath ?? firstImage?.imageUrl ?? firstImage?.path;
    if (!source) return "";
    if (/^https?:\/\//i.test(source)) return source;
    return `${apiBase}${source.startsWith("/") ? source : `/${source}`}`;
  }

  async function deleteProduct(id: string) {
    await adminFetch(`/api/admin/products/${encodeURIComponent(id)}`, { method: "DELETE" });
    await loadProducts();
  }

  async function toggleProductStatus(id: string, active: boolean) {
    await adminFetch(`/api/admin/products/${encodeURIComponent(id)}/toggle`, {
      method: "POST",
      body: JSON.stringify({ active })
    });
    await loadProducts();
  }

  async function updateProductPrice(id: string, priceCents: number | null): Promise<void> {
    await adminFetch(`/api/admin/products/${encodeURIComponent(id)}/price`, {
      method: "PATCH",
      body: JSON.stringify({ priceCents })
    });
  }

  async function requestOrderRefund(orderNo: string, note?: string) {
    const response = await adminFetch(`/api/admin/orders/${encodeURIComponent(orderNo)}/refund`, {
      method: "POST",
      body: JSON.stringify({ note: note?.trim() || undefined }),
    });
    const result = await response.json();
    await loadOrders();
    return result;
  }

  return {
    apiBase, products, categories, orders, quotes,
    loading, orderLoading, quoteLoading, publishLoading, storeSaving, errorMessage,
    activeMenu, targetOrderTab, adminPasscode, adminSessionToken, adminSessionExpiresAt, adminTokenVerified, adminSessionChecking, adminLoginLoading, notificationStatus,
    storeProfile, quoteDrafts,
    apiReady, pendingShipments, pickupReady, pendingRefunds, pendingAdjustments, pendingTasksCount, unreadOrderEvents, notificationRecipients, notificationBinding,
    adminFetch, loginAdmin, logoutAdmin, onTokenInput, loadProducts, loadCategories, loadQuotes, loadOrders, loadStore,
    refreshCurrent, selectMenu, startSessionMonitor, stopSessionMonitor, clearUnreadOrderEvents, loadNotificationStatus, loadNotificationRecipients, createNotificationBinding, disableNotificationRecipient, enableDesktopNotifications, yuanToCents, centsInput, money, productImageUrl, deleteProduct, toggleProductStatus, updateProductPrice, requestOrderRefund
  };
});
